// FeeTransactions.jsx

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { SweetAlertShow } from "../../../master/Master";
import { studentDetailsTransactions } from "../../../../api/StudentDetails";
import { studentFeeTransactionDeleteV4 } from "../../../../api/Student";
import usePrivateLink from "../../../config/usePrivateLink";
import { formatToDateTime } from "../../../master/FormatToDateTime";
import ReceiptNoUpdateModal from "../../../studentFeeReport/components/ReceiptNoUpdateModal";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const StatCard = ({ icon, color, label, value, loading }) => (
  <div className="col-md-3 col-6">
    <div
      className="p-3 bg-white rounded-4 h-100"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <div
          className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10`}
          style={{ width: 38, height: 38 }}
        >
          <i className={`${icon} text-${color} fs-5`}></i>
        </div>
        <small className="text-secondary fw-medium">{label}</small>
      </div>
      {loading ? (
        <div className="placeholder-glow">
          <Skeleton w="80px" h="22px" />
        </div>
      ) : (
        <h4 className="mb-0 fw-bold">{value}</h4>
      )}
    </div>
  </div>
);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString()}`;

const getStatusMeta = (status) => {
  switch (String(status)) {
    case "1":
      return {
        label: "Success",
        color: "success",
        icon: "ri-checkbox-circle-fill",
      };
    case "2":
      return { label: "Pending", color: "warning", icon: "ri-time-line" };
    case "3":
      return { label: "Deleted", color: "danger", icon: "ri-delete-bin-line" };
    default:
      return { label: "Unknown", color: "secondary", icon: "ri-question-line" };
  }
};

const paymentModeIcon = (mode) => {
  switch ((mode || "").toLowerCase()) {
    case "cash":
      return "ri-cash-line";
    case "online":
      return "ri-bank-card-line";
    case "upi":
      return "ri-qr-code-line";
    case "cheque":
      return "ri-file-paper-line";
    default:
      return "ri-wallet-3-line";
  }
};

const parseModule = (raw) => {
  if (!raw) return [];
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return [];
  }
};

const TABS = [
  { key: "all", label: "All", icon: "ri-list-check", color: "primary" },
  {
    key: "1",
    label: "Success",
    icon: "ri-checkbox-circle-line",
    color: "success",
  },
  { key: "2", label: "Pending", icon: "ri-time-line", color: "warning" },
  { key: "3", label: "Deleted", icon: "ri-delete-bin-line", color: "danger" },
];

export default function FeeTransactions({ id }) {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const getStudentDetailsMain = async () => {
    setLoading(true);
    try {
      const response = await studentDetailsTransactions(id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain();
  }, []);

  const handleDelete = async (txnId) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This Transaction will be permanently deleted. You cannot undo this action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const response = await studentFeeTransactionDeleteV4(txnId);
      if (response.data.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getStudentDetailsMain();
    }
  };

  const allTransactions = useMemo(() => {
    const active = data?.transactions || [];
    const deleted = data?.deleted_transactions || [];
    return [...active, ...deleted].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [data]);

  const counts = useMemo(() => {
    const c = { all: allTransactions.length, 1: 0, 2: 0, 3: 0 };
    allTransactions.forEach((t) => {
      const s = String(t.status);
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [allTransactions]);

  const filteredTransactions = useMemo(() => {
    if (activeTab === "all") return allTransactions;
    return allTransactions.filter((t) => String(t.status) === activeTab);
  }, [allTransactions, activeTab]);

  const summary = useMemo(() => {
    const active = data?.transactions || [];
    const successTxns = active.filter((t) => String(t.status) === "1");
    const totalPaid = successTxns.reduce(
      (sum, t) => sum + Number(t.amount || 0),
      0,
    );
    const lastTxn = [...active].sort(
      (a, b) => new Date(b.payment_date) - new Date(a.payment_date),
    )[0];
    return {
      totalPaid,
      count: active.length,
      lastAmount: lastTxn?.amount,
      lastReceipt: lastTxn?.receipt_no,
    };
  }, [data]);

  return (
    <>
      <div className="row g-3">
        {/* Payment Summary */}
        <StatCard
          icon="ri-money-dollar-circle-line"
          color="success"
          label="Total Paid"
          loading={loading}
          value={fmtMoney(summary.totalPaid)}
        />
        <StatCard
          icon="ri-exchange-line"
          color="info"
          label="Transactions"
          loading={loading}
          value={summary.count}
        />
        <StatCard
          icon="ri-history-line"
          color="warning"
          label="Last Payment"
          loading={loading}
          value={summary.lastAmount ? fmtMoney(summary.lastAmount) : "-"}
        />
        <StatCard
          icon="ri-file-list-3-line"
          color="secondary"
          label="Last Receipt"
          loading={loading}
          value={summary.lastReceipt || "-"}
        />

        {/* Payment History */}
        <div className="col-12">
          <div
            className="p-3 bg-white rounded-4"
            style={{ border: "1px solid #eef0f4" }}
          >
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 bg-success bg-opacity-10"
                  style={{ width: 38, height: 38 }}
                >
                  <i className="ri-money-dollar-circle-line text-success fs-5"></i>
                </div>
                <h6 className="mb-0 fw-semibold">Fee Transactions</h6>
              </div>
            </div>

            {/* Status Tabs */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    className={`btn btn-sm d-flex align-items-center gap-1 ${
                      isActive ? `btn-${tab.color}` : `btn-outline-${tab.color}`
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                    <span
                      className={`badge rounded-pill ${
                        isActive
                          ? "bg-white text-dark"
                          : `bg-${tab.color} bg-opacity-10 text-${tab.color}`
                      }`}
                    >
                      {loading ? "-" : (counts[tab.key] ?? 0)}
                    </span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="placeholder-glow d-flex flex-column gap-2">
                <Skeleton h="20px" />
                <Skeleton h="20px" />
                <Skeleton h="20px" />
              </div>
            ) : !filteredTransactions.length ? (
              <div className="text-center py-5 text-muted">
                <i className="ri-inbox-line d-block fs-1 mb-2"></i>
                <p className="mb-0">No transactions found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>
                        <i className="ri-file-list-3-line me-1"></i>Receipt No
                      </th>
                      <th>
                        <i className="ri-calendar-line me-1"></i>Date
                      </th>
                      <th>
                        <i className="ri-price-tag-3-line me-1"></i>Fee Type
                      </th>
                      <th>
                        <i className="ri-wallet-3-line me-1"></i>Payment Mode
                      </th>
                      <th>
                        <i className="ri-money-rupee-circle-line me-1"></i>
                        Amount
                      </th>
                      <th>
                        <i className="ri-user-star-line me-1"></i>Collected By
                      </th>
                      <th>
                        <i className="ri-checkbox-circle-line me-1"></i>Status
                      </th>
                      <th width="120">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredTransactions.map((txn, index) => {
                      const meta = getStatusMeta(txn.status);
                      const modules = parseModule(txn.module);
                      const isDeleted = String(txn.status) === "3";

                      return (
                        <tr
                          key={txn.id}
                          className={
                            isDeleted ? "table-danger bg-opacity-25" : ""
                          }
                        >
                          <td>{index + 1}</td>

                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="fw-semibold text-nowrap">
                                {txn.receipt_no}
                              </span>
                              {hasAccess(
                                "/student/fee/transaction/receipt-no/update",
                              ) && (
                                <i
                                  className="ri-edit-line cursor-pointer text-info"
                                  title="Update Receipt No"
                                  onClick={() => setSelectedTransaction(txn)}
                                ></i>
                              )}
                            </div>
                          </td>
                          <td>{fmtDate(txn.payment_date)}</td>
                          <td>
                            {modules.length ? (
                              <div className="d-flex flex-wrap gap-1">
                                {modules.map((m, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-info bg-opacity-10 text-primary"
                                  >
                                    #{m?.ids} {m.type} : {fmtMoney(m.amount)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <span className="badge bg-info bg-opacity-10 text-info d-inline-flex align-items-center gap-1">
                              <i
                                className={paymentModeIcon(txn.payment_method)}
                              ></i>
                              {txn.payment_method || "-"}
                            </span>
                          </td>
                          <td className="fw-bold text-success">
                            {fmtMoney(txn.amount)}
                          </td>
                          <td>
                            <small className="d-flex align-items-center gap-1">
                              <i className="ri-user-line text-secondary"></i>
                              {txn.created_by?.name || "-"}
                            </small>
                            <small className="d-flex align-items-center gap-1 text-muted">
                              <i className="ri-calendar-event-line text-secondary"></i>
                              {formatToDateTime(txn.created_at)}
                            </small>
                          </td>

                          {/* Status + Deleted Info */}
                          <td>
                            <span
                              className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                            >
                              <i className={meta.icon}></i>
                              {meta.label}
                            </span>

                            {isDeleted && (
                              <div className="mt-1">
                                {txn.deleted_by?.name && (
                                  <small className="d-flex align-items-center gap-1 text-secondary">
                                    <i className="ri-user-unfollow-line"></i>
                                    By: {txn.deleted_by.name}
                                  </small>
                                )}
                                {txn.deleted_reason && (
                                  <small className="d-flex align-items-center gap-1 text-secondary">
                                    <i className="ri-message-2-line"></i>
                                    {txn.deleted_reason}
                                  </small>
                                )}
                                <small className="d-flex align-items-center gap-1 text-muted">
                                  <i className="ri-time-line"></i>
                                  {formatToDateTime(txn.updated_at)}
                                </small>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-end">
                              {hasAccess("/student/fees/receipt/print") && (
                                <Link
                                  className="btn btn-label-primary btn-sm"
                                  to={`/student/fees/receipt?id=${txn?.id}`}
                                  title="Print receipt"
                                >
                                  <i className="ri-printer-line ri-20px"></i>
                                </Link>
                              )}

                              {!isDeleted &&
                                hasAccess(
                                  "/student/fee/transaction/delete",
                                ) && (
                                  <button
                                    className="btn btn-label-danger btn-sm"
                                    onClick={() => handleDelete(txn?.id)}
                                    title="Delete transaction"
                                  >
                                    <i className="ri-delete-bin-line ri-20px"></i>
                                  </button>
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedTransaction && (
        <ReceiptNoUpdateModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onUpdated={() => {
            setSelectedTransaction(null);
            getStudentDetailsMain();
          }}
        />
      )}
    </>
  );
}
